<template>
  <div class="ref-section">
    <!-- Collapsed strip -->
    <button type="button" class="ref-strip" @click="toggleExpanded">
      <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
      </svg>
      <span class="ref-strip-label">References</span>
      <span v-if="documents.length > 0" class="ref-count">{{ documents.length }}</span>
      <span v-if="hasVariants" class="ref-shared-hint">shared across all alternatives</span>
      <svg class="w-3 h-3 ml-auto transition-transform" :class="{ 'rotate-180': isExpanded }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
      </svg>
    </button>

    <!-- Expanded content -->
    <div v-if="isExpanded" class="mt-2 space-y-2">
      <!-- Document list -->
      <div v-for="doc in documents" :key="doc.id" class="ref-doc-row">
        <svg class="w-3.5 h-3.5 text-zinc-300 dark:text-zinc-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        <span class="text-xs text-zinc-700 dark:text-zinc-300 truncate">{{ doc.title }}</span>
        <span class="text-[10px] text-zinc-400 flex-shrink-0">{{ doc.file_size_human }}</span>
        <div class="ml-auto flex items-center gap-1 flex-shrink-0">
          <button type="button" @click="downloadDocument(doc)" class="ref-doc-action" title="Download">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
            </svg>
          </button>
          <button type="button" @click="deleteDocument(doc)" class="ref-doc-action hover:!text-red-500" title="Delete">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Upload zone -->
      <div
        class="ref-upload-zone"
        :class="{ 'ref-upload-zone--active': isDragging }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="$refs.fileInput.click()"
      >
        <input
          ref="fileInput"
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt"
          class="hidden"
          @change="handleFileSelect"
        />
        <svg v-if="!isUploading" class="w-4 h-4 text-zinc-300 dark:text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
        </svg>
        <div v-else class="w-4 h-4 border-2 border-zinc-200 border-t-gold rounded-full animate-spin"></div>
        <span class="text-xs text-zinc-400 dark:text-zinc-500">
          {{ isUploading ? 'Uploading…' : 'Drop certificates, letters or reviews here — or click to browse' }}
        </span>
      </div>

      <p v-if="error" class="text-xs text-red-500">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import { reportUpgradeRequired } from '../services/ApiService.js'

const ENDPOINT_BY_TYPE = {
  experience: 'master_experience_entries',
  education: 'master_education_entries',
  activity: 'master_activities'
}

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB, matches backend validation

export default {
  name: 'MasterReferenceSection',
  props: {
    // Canonical master entry id — documents are shared across its variant group
    entryId: {
      type: [String, Number],
      required: true
    },
    entryType: {
      type: String,
      required: true,
      validator: (v) => ['experience', 'education', 'activity'].includes(v)
    },
    hasVariants: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      documents: [],
      isExpanded: false,
      isUploading: false,
      isDragging: false,
      error: ''
    }
  },
  computed: {
    baseUrl() {
      return `/api/${ENDPOINT_BY_TYPE[this.entryType]}/${this.entryId}/reference_documents`
    }
  },
  async mounted() {
    await this.loadDocuments()
  },
  methods: {
    getCSRFToken() {
      const meta = document.querySelector('meta[name="csrf-token"]')
      return meta ? meta.getAttribute('content') : ''
    },

    async loadDocuments() {
      try {
        const res = await fetch(this.baseUrl, { headers: { Accept: 'application/json' } })
        if (res.ok) {
          const data = await res.json()
          this.documents = data.reference_documents || []
        }
      } catch (e) {
        console.error('Failed to load reference documents:', e)
      }
    },

    toggleExpanded() {
      this.isExpanded = !this.isExpanded
      this.error = ''
    },

    handleDrop(e) {
      this.isDragging = false
      this.uploadFiles(Array.from(e.dataTransfer.files))
    },

    handleFileSelect(e) {
      this.uploadFiles(Array.from(e.target.files))
      e.target.value = ''
    },

    async uploadFiles(files) {
      if (!files.length || this.isUploading) return

      const oversized = files.find(f => f.size > MAX_FILE_SIZE)
      if (oversized) {
        this.error = `${oversized.name} is larger than 10MB`
        return
      }

      this.isUploading = true
      this.error = ''

      try {
        const formData = new FormData()
        files.forEach(file => formData.append('reference_documents[]', file))

        const res = await fetch(this.baseUrl, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-CSRF-Token': this.getCSRFToken() },
          body: formData
        })
        const data = await res.json()

        if (res.status === 402) {
          // Pro feature — open the global UpgradeModal
          reportUpgradeRequired(data)
          this.error = data.message || 'Upgrade required'
          return
        }

        if (data.success) {
          await this.loadDocuments()
          if (data.errors?.length) this.error = data.errors.join(', ')
        } else {
          this.error = data.message || 'Upload failed'
        }
      } catch (e) {
        console.error('Reference upload failed:', e)
        this.error = 'Upload failed. Please try again.'
      } finally {
        this.isUploading = false
      }
    },

    async downloadDocument(doc) {
      try {
        const res = await fetch(`${this.baseUrl}/${doc.id}/download`, {
          headers: { 'X-CSRF-Token': this.getCSRFToken() }
        })
        if (!res.ok) throw new Error('Download failed')
        const blob = await res.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = doc.original_filename || doc.title
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } catch (e) {
        console.error('Download failed:', e)
        this.error = 'Download failed. Please try again.'
      }
    },

    async deleteDocument(doc) {
      try {
        const res = await fetch(`${this.baseUrl}/${doc.id}`, {
          method: 'DELETE',
          headers: { Accept: 'application/json', 'X-CSRF-Token': this.getCSRFToken() }
        })
        const data = await res.json()
        if (data.success) {
          this.documents = this.documents.filter(d => d.id !== doc.id)
        } else {
          this.error = data.message || 'Failed to delete document'
        }
      } catch (e) {
        console.error('Delete failed:', e)
        this.error = 'Failed to delete document'
      }
    }
  }
}
</script>

<style scoped>
.ref-strip {
  @apply w-full flex items-center gap-1.5 px-3 py-1.5 border border-zinc-100 dark:border-zinc-800 rounded-xl text-zinc-400 dark:text-zinc-500 hover:border-zinc-300 dark:hover:border-zinc-600 transition-colors cursor-pointer bg-transparent;
}

.ref-strip-label {
  @apply text-[10px] font-semibold uppercase tracking-widest;
}

.ref-count {
  @apply w-4 h-4 rounded-full bg-gold text-white text-[10px] font-semibold flex items-center justify-center leading-none;
}

.ref-shared-hint {
  @apply text-[10px] text-zinc-300 dark:text-zinc-600 normal-case tracking-normal;
}

.ref-doc-row {
  @apply flex items-center gap-2 px-3 py-2 border border-zinc-100 dark:border-zinc-800 rounded-xl;
}

.ref-doc-action {
  @apply p-1 rounded-lg text-zinc-300 dark:text-zinc-600 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors;
}

.ref-upload-zone {
  @apply flex items-center justify-center gap-2 px-3 py-3 border border-dashed border-zinc-200 dark:border-zinc-700 rounded-xl cursor-pointer hover:border-gold dark:hover:border-gold-600 transition-colors;
}

.ref-upload-zone--active {
  @apply border-solid border-gold bg-gold-50/40 dark:bg-gold-900/10;
}
</style>
